import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Navigatie           from './components/Navigatie';
import Login               from './pages/Login';
import TwofaSetup          from './pages/TwofaSetup';
import WachtwoordInstellen from './pages/WachtwoordInstellen';
import WachtwoordVergeten  from './pages/WachtwoordVergeten';
import Dashboard           from './pages/Dashboard';
import Beoordeling         from './pages/Beoordeling';
import Updates       from './pages/Updates';
import Profiel       from './pages/Profiel';
import OrgOutputs    from './pages/OrgOutputs';
import MijnOutputs         from './pages/MijnOutputs';
import Gebruikersbeheer    from './pages/Gebruikersbeheer';
import RollenBeheer        from './pages/RollenBeheer';
import LocatiesBeheer      from './pages/LocatiesBeheer';
import Superadmin          from './pages/Superadmin';

function Laden() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      minHeight:'100vh', fontFamily:'Arial', color:'#1B4F8A', fontSize:18 }}>
      Laden...
    </div>
  );
}

// Basis beschermde route — vereist inloggen + 2FA
function BeschermdeRoute({ children }) {
  const { gebruiker, laden } = useAuth();
  if (laden) return <Laden />;
  if (!gebruiker) return <Navigate to="/login" replace />;
  if (!gebruiker.twofa_actief && window.location.pathname !== '/2fa-setup') {
    return <Navigate to="/2fa-setup" replace />;
  }
  return children;
}

// Beschermde route met permissiecheck — stuurt door naar dashboard bij geen toegang
function PermissieRoute({ children, permissie }) {
  const { gebruiker, laden, heeftPermissie } = useAuth();
  if (laden) return <Laden />;
  if (!gebruiker) return <Navigate to="/login" replace />;
  if (!gebruiker.twofa_actief && window.location.pathname !== '/2fa-setup') {
    return <Navigate to="/2fa-setup" replace />;
  }
  if (!heeftPermissie(permissie)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}


// Locaties route — vereist permissie EN organisatie met locatie/afdeling hiërarchie
function LocatiesRoute({ children }) {
  const { gebruiker, laden, heeftPermissie } = useAuth();
  if (laden) return <Laden />;
  if (!gebruiker) return <Navigate to="/login" replace />;
  if (!gebruiker.twofa_actief && window.location.pathname !== '/2fa-setup') {
    return <Navigate to="/2fa-setup" replace />;
  }
  // Superadmin heeft altijd toegang
  if (heeftPermissie('superadmin.alles')) return children;
  // Organisatiebeheerder alleen als hiërarchie locaties of afdelingen heeft
  if (!heeftPermissie('beheer.instellingen')) return <Navigate to="/dashboard" replace />;
  if (gebruiker.hierarchie === 'plat') return <Navigate to="/dashboard" replace />;
  return children;
}

function OpenbaareRoute({ children }) {
  const { gebruiker, laden } = useAuth();
  if (laden) return null;
  return gebruiker ? <Navigate to="/dashboard" replace /> : children;
}

function MetNavigatie({ children }) {
  const { gebruiker } = useAuth();
  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:'#F5F7FA' }}>
      {gebruiker && <Navigatie />}
      <main style={{ flex:1 }}>
        {children}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Openbare routes */}
          <Route path="/login"                element={<OpenbaareRoute><Login /></OpenbaareRoute>} />
          <Route path="/wachtwoord-instellen" element={<WachtwoordInstellen />} />
          <Route path="/wachtwoord-vergeten"  element={<WachtwoordVergeten />} />

          {/* Beveiligde routes — alleen inloggen vereist */}
          <Route path="/2fa-setup"    element={<BeschermdeRoute><MetNavigatie><TwofaSetup /></MetNavigatie></BeschermdeRoute>} />
          <Route path="/dashboard"    element={<BeschermdeRoute><MetNavigatie><Dashboard /></MetNavigatie></BeschermdeRoute>} />
          <Route path="/beoordeling"  element={<BeschermdeRoute><MetNavigatie><Beoordeling /></MetNavigatie></BeschermdeRoute>} />
          <Route path="/profiel" element={<BeschermdeRoute><MetNavigatie><Profiel /></MetNavigatie></BeschermdeRoute>} />
        <Route path="/org-outputs" element={<BeschermdeRoute><MetNavigatie><OrgOutputs /></MetNavigatie></BeschermdeRoute>} />
        <Route path="/updates" element={<BeschermdeRoute><MetNavigatie><Updates /></MetNavigatie></BeschermdeRoute>} />
        <Route path="/outputs"      element={<BeschermdeRoute><MetNavigatie><MijnOutputs /></MetNavigatie></BeschermdeRoute>} />

          {/* Beheer routes — permissie vereist */}
          <Route path="/admin/gebruikers" element={
            <PermissieRoute permissie="beheer.gebruikers">
              <MetNavigatie><Gebruikersbeheer /></MetNavigatie>
            </PermissieRoute>
          } />
          <Route path="/admin/rollen" element={
            <PermissieRoute permissie="beheer.rollen">
              <MetNavigatie><RollenBeheer /></MetNavigatie>
            </PermissieRoute>
          } />
          <Route path="/admin/locaties" element={
            <LocatiesRoute>
              <MetNavigatie><LocatiesBeheer /></MetNavigatie>
            </LocatiesRoute>
          } />
          <Route path="/admin" element={
            <PermissieRoute permissie="beheer.gebruikers">
              <MetNavigatie><Dashboard /></MetNavigatie>
            </PermissieRoute>
          } />

          {/* Superadmin route */}
          <Route path="/superadmin" element={
            <PermissieRoute permissie="superadmin.alles">
              <MetNavigatie><Superadmin /></MetNavigatie>
            </PermissieRoute>
          } />

          <Route path="/"  element={<Navigate to="/dashboard" replace />} />
          <Route path="*"  element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false}
        newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </AuthProvider>
  );
}

export default App;
